const API_BASE = import.meta.env.VITE_API_URL || '';

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string;
};

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const msg = error.detail ? `${error.error}: ${error.detail}` : (error.error || error.message || 'Request failed');
    throw new ApiError(response.status, msg);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// =============================================================================
// Apps API
// =============================================================================

export interface App {
  id: string;
  name: string;
  slug: string;
  category?: string | null;
  status: 'published' | 'on_deck' | 'gestating' | 'archived';
  revenueOwner: 'charsoft' | 'csl' | 'partnership';
  partnershipId: string | null;
  monetization: {
    type: 'stripe_subscription' | 'stripe_onetime' | 'admob' | 'none';
    stripeProductIds: string[];
    admobAppId: string | null;
  };
  gcpProjectId: string | null;
  githubRepo: string | null;
  liveUrl: string | null;
  description: string;
  notes: string | null;
  thumbnail: string | null;
  techStack: string[];
  createdAt: string;
  updatedAt: string;
  launchedAt: string | null;
}

export interface AppsResponse {
  apps: App[];
}

export const appsApi = {
  list: (token: string, filters?: { status?: string; revenueOwner?: string; category?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.revenueOwner) params.set('revenueOwner', filters.revenueOwner);
    if (filters?.category) params.set('category', filters.category);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<AppsResponse>(`/api/apps${query}`, { token });
  },

  get: (token: string, id: string) => {
    return request<App>(`/api/apps/${id}`, { token });
  },

  create: (token: string, data: Omit<App, 'id' | 'createdAt' | 'updatedAt'>) => {
    return request<App>('/api/apps', { method: 'POST', body: data, token });
  },

  update: (token: string, id: string, data: Partial<App>) => {
    return request<App>(`/api/apps/${id}`, { method: 'PATCH', body: data, token });
  },

  delete: (token: string, id: string, hard = false) => {
    const query = hard ? '?hard=true' : '';
    return request<void>(`/api/apps/${id}${query}`, { method: 'DELETE', token });
  },
};

// =============================================================================
// Tasks API (stubbed)
// =============================================================================

export interface Task {
  id: string;
  title: string;
  description: string | null;
  board: 'csl' | 'charsoft' | 'personal';
  status: 'todo' | 'doing' | 'done' | 'archived';
  priority: 'urgent' | 'normal' | 'low';
  dueDate: string | null;
  completedAt: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export const tasksApi = {
  list: (token: string, filters?: { board?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.board) params.set('board', filters.board);
    if (filters?.status) params.set('status', filters.status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<{ tasks: Task[] }>(`/api/tasks${query}`, { token });
  },

  create: (token: string, data: Partial<Task>) => {
    return request<Task>('/api/tasks', { method: 'POST', body: data, token });
  },

  update: (token: string, id: string, data: Partial<Task>) => {
    return request<Task>(`/api/tasks/${id}`, { method: 'PATCH', body: data, token });
  },

  move: (token: string, id: string, status: Task['status']) => {
    return request<Task>(`/api/tasks/${id}/move`, { method: 'POST', body: { status }, token });
  },

  delete: (token: string, id: string) => {
    return request<void>(`/api/tasks/${id}`, { method: 'DELETE', token });
  },
};

// =============================================================================
// Revenue API (stubbed)
// =============================================================================

export const revenueApi = {
  summary: (token: string, params?: { period?: string; year?: number; quarter?: number; month?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.period) searchParams.set('period', params.period);
    if (params?.year) searchParams.set('year', params.year.toString());
    if (params?.quarter) searchParams.set('quarter', params.quarter.toString());
    if (params?.month) searchParams.set('month', params.month.toString());
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return request<unknown>(`/api/revenue/summary${query}`, { token });
  },

  transactions: (token: string) => {
    return request<unknown>('/api/revenue/transactions', { token });
  },
};

// =============================================================================
// Dashboard API (stubbed)
// =============================================================================

export interface UrgentDeadline {
  title: string;
  daysRemaining: number;
  dueDate: string;
  sourceType: 'pipeline' | 'task';
  sourceId: string;
}

export interface FocusData {
  urgentDeadlines: UrgentDeadline[];
  social: {
    postsCompleted: number;
    totalPlatforms: number;
    weekId: string;
  };
  streak: {
    current: number;
    longest: number;
  };
  counts: {
    tasksInProgress: number;
    activePipelineItems: number;
    ideasInPipeline: number;
  };
}

export const dashboardApi = {
  focus: (token: string) => {
    return request<FocusData>('/api/dashboard/focus', { token });
  },

  alerts: (token: string) => {
    return request<unknown>('/api/dashboard/alerts', { token });
  },

  stats: (token: string) => {
    return request<unknown>('/api/dashboard/stats', { token });
  },
};

// =============================================================================
// Social Tracking API
// =============================================================================

export interface SocialPlatform {
  posted: boolean;
  postedAt: string | null;
  url: string | null;
}

export interface ChannelFlow {
  talkUploaded: boolean;
  uploadedAt: string | null;
  contentGenerated: boolean;
  generatedAt: string | null;
  shortsCount: number;
  firebaseDocId: string | null;
}

export interface SocialWeek {
  id: string;
  weekStart: string;
  weekEnd: string;
  channelFlow: ChannelFlow;
  platforms: {
    facebook: SocialPlatform;
    instagram: SocialPlatform;
    linkedin: SocialPlatform;
    substack: SocialPlatform;
    tiktok: SocialPlatform;
    email: SocialPlatform & { campaignId?: string | null };
  };
  completionCount: number;
  totalPlatforms: number;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Streak {
  id: string;
  name: string;
  currentStreak: number;
  longestStreak: number;
  lastCompletedWeek: string | null;
  updatedAt?: string;
}

export interface ChannelFlowRef {
  projectId: string;
  videoId: string;
  detectedAt: string | null;
}

export interface QuoteVisual {
  quote: string;
  image_url?: string;
}

export interface ShortsCandidate {
  title: string;
  start_time: number;
  end_time: number;
  hook?: string;
}

export interface SourceContent {
  facebook: string | null;
  facebookTitle: string | null;
  linkedin: string | null;
  linkedinTitle: string | null;
  email: string | null;
  emailTitle: string | null;
  substack: string | null;
  substackTitle: string | null;
  quoteVisuals: QuoteVisual[];
  shortsCandidates: ShortsCandidate[];
}

export interface WeekContentResponse {
  weekId: string;
  channelFlowRef: ChannelFlowRef | null;
  sourceContent: SourceContent | null;
  platforms: SocialWeek['platforms'];
}

// =============================================================================
// Pipeline API
// =============================================================================

export interface PipelineNote {
  date: string;
  content: string;
}

export interface PipelineItem {
  id: string;
  title: string;
  description: string | null;
  stage: 'idea' | 'proposal' | 'active' | 'complete' | 'lost';
  partnerOrg: string | null;
  partnerContact: string | null;
  partnerEmail: string | null;
  estimatedValue: number | null;
  actualValue: number | null;
  valueType: 'one_time' | 'recurring' | 'tbd';
  deadline: string | null;
  startDate: string | null;
  completedAt: string | null;
  proposalUrl: string | null;
  agreementUrl: string | null;
  notes: PipelineNote[];
  createdAt: string;
  updatedAt: string;
}

export const pipelineApi = {
  list: (token: string, filters?: { stage?: string }) => {
    const params = new URLSearchParams();
    if (filters?.stage) params.set('stage', filters.stage);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<{ items: PipelineItem[] }>(`/api/pipeline${query}`, { token });
  },

  get: (token: string, id: string) => {
    return request<{ item: PipelineItem }>(`/api/pipeline/${id}`, { token });
  },

  create: (token: string, data: Partial<PipelineItem>) => {
    return request<{ item: PipelineItem }>('/api/pipeline', { method: 'POST', body: data, token });
  },

  update: (token: string, id: string, data: Partial<PipelineItem>) => {
    return request<{ item: PipelineItem }>(`/api/pipeline/${id}`, { method: 'PATCH', body: data, token });
  },

  delete: (token: string, id: string) => {
    return request<void>(`/api/pipeline/${id}`, { method: 'DELETE', token });
  },

  move: (token: string, id: string, stage: PipelineItem['stage']) => {
    return request<{ item: PipelineItem }>(`/api/pipeline/${id}/move`, { method: 'POST', body: { stage }, token });
  },

  addNote: (token: string, id: string, content: string) => {
    return request<{ item: PipelineItem }>(`/api/pipeline/${id}/notes`, { method: 'POST', body: { content }, token });
  },
};

// =============================================================================
// Prospects API
// =============================================================================

export interface Prospect {
  id: string;
  company: string;
  hqCity: string | null;
  contactName: string | null;
  contactTitle: string | null;
  segment: 'ai_data_consultancy' | 'staff_aug' | 'bank_credit_union' | 'insurer' | 'it_integrator' | 'marketing_cx_agency';
  aiMaturity: 'exploring' | 'piloting' | 'in_production' | null;
  leadSource: string | null;
  stage: 'research' | 'first_outreach' | 'discovery_call' | 'needs_analysis' | 'proposal_sent' | 'negotiation' | 'closed_won' | 'closed_lost';
  lastTouchDate: string | null;
  nextAction: string | null;
  estDealValue: number | null;
  winProbability: number | null;
  tierOffered: string | null;
  notes: string | null;
  linkedinUrl: string | null;
  websiteUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachActivityItem {
  id: string;
  prospectId: string;
  date: string;
  activityType: 'linkedin_dm' | 'email' | 'linkedin_engage' | 'call' | 'meeting' | 'other';
  channel: 'linkedin' | 'email' | 'phone' | 'in_person' | 'other';
  summary: string;
  nextStep: string | null;
  nextStepDate: string | null;
  createdAt: string;
}

export interface ProspectStats {
  totalProspects: number;
  countsByStage: Record<string, number>;
  totalValue: number;
  weightedValue: number;
}

export const prospectsApi = {
  list: (token: string, filters?: { segment?: string; stage?: string }) => {
    const params = new URLSearchParams();
    if (filters?.segment) params.set('segment', filters.segment);
    if (filters?.stage) params.set('stage', filters.stage);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<{ items: Prospect[] }>(`/api/prospects${query}`, { token });
  },

  get: (token: string, id: string) => {
    return request<{ item: Prospect }>(`/api/prospects/${id}`, { token });
  },

  create: (token: string, data: Partial<Prospect>) => {
    return request<{ item: Prospect }>('/api/prospects', { method: 'POST', body: data, token });
  },

  update: (token: string, id: string, data: Partial<Prospect>) => {
    return request<{ item: Prospect }>(`/api/prospects/${id}`, { method: 'PATCH', body: data, token });
  },

  delete: (token: string, id: string) => {
    return request<void>(`/api/prospects/${id}`, { method: 'DELETE', token });
  },

  move: (token: string, id: string, stage: Prospect['stage']) => {
    return request<{ item: Prospect }>(`/api/prospects/${id}/move`, { method: 'POST', body: { stage }, token });
  },

  getActivity: (token: string, id: string) => {
    return request<{ activities: OutreachActivityItem[] }>(`/api/prospects/${id}/activity`, { token });
  },

  logActivity: (token: string, id: string, data: {
    activityType: OutreachActivityItem['activityType'];
    channel: OutreachActivityItem['channel'];
    summary: string;
    nextStep?: string;
    nextStepDate?: string;
  }) => {
    return request<{ activity: OutreachActivityItem }>(`/api/prospects/${id}/activity`, { method: 'POST', body: data, token });
  },

  stats: (token: string) => {
    return request<ProspectStats>('/api/prospects/stats', { token });
  },
};

// =============================================================================
// Campaigns API
// =============================================================================

export interface DemoUser {
  id: string;
  name?: string;
  email?: string;
  createdAt?: string;
  lastActive?: string;
  feedbackEmailStatus?: 'not_sent' | 'sent' | 'opened' | 'failed';
  feedbackEmailSentAt?: string;
  feedbackEmailOpenedAt?: string;
  feedbackEmailSubject?: string;
  feedbackEmailBodyPreview?: string;
  feedbackEmailSender?: string;
}

export interface DemoFeedbackMetrics {
  total: number;
  not_sent: number;
  sent: number;
  opened: number;
  failed: number;
}

export interface DemoUsersResponse {
  items: DemoUser[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  metrics: DemoFeedbackMetrics;
}

export const campaignsApi = {
  listDemoUsers: (token: string, filters?: { page?: number; pageSize?: number; status?: string; search?: string }) => {
    const params = new URLSearchParams();
    if (filters?.page) params.set('page', filters.page.toString());
    if (filters?.pageSize) params.set('pageSize', filters.pageSize.toString());
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<DemoUsersResponse>(`/api/campaigns/demo-feedback/users${query}`, { token });
  },

  sendFeedbackEmail: (token: string, userId: string, data: { subject: string; body: string; surveyLink: string; sender: string }) => {
    return request<{ item: DemoUser }>(`/api/campaigns/demo-feedback/${userId}/send`, { method: 'POST', body: data, token });
  },

  sendBulkFeedbackEmail: (token: string, userIds: string[], data: { subject: string; body: string; surveyLink: string; sender: string }) => {
    return request<{ processed: number; sent: number; failed: number }>(`/api/campaigns/demo-feedback/send-bulk`, { 
      method: 'POST', 
      body: { ...data, userIds }, 
      token 
    });
  },

  sendTestFeedbackEmail: (token: string, data: { subject: string; body: string; surveyLink: string; sender: string }) => {
    return request<{ success: true; email: string }>(`/api/campaigns/demo-feedback/send-test`, { 
      method: 'POST', 
      body: data, 
      token 
    });
  },

  syncCampaignStats: (token: string) => {
    return request<{ success: true; updated: number }>(`/api/campaigns/demo-feedback/sync-stats`, { 
      method: 'POST', 
      token 
    });
  },
};

export const socialApi = {
  getCurrentWeek: (token: string) => {
    return request<{ week: SocialWeek }>('/api/social/current-week', { token });
  },

  getWeek: (token: string, weekId: string) => {
    return request<{ week: SocialWeek }>(`/api/social/week/${weekId}`, { token });
  },

  getWeekContent: (token: string, weekId: string) => {
    return request<WeekContentResponse>(`/api/social/week/${weekId}/content`, { token });
  },

  updatePlatform: (
    token: string,
    weekId: string,
    platform: string,
    data: { posted: boolean; url?: string | null }
  ) => {
    return request<{ week: SocialWeek }>(
      `/api/social/week/${weekId}/platform/${platform}`,
      { method: 'PATCH', body: data, token }
    );
  },

  updateChannelFlow: (
    token: string,
    weekId: string,
    data: { talkUploaded?: boolean; contentGenerated?: boolean; shortsCount?: number }
  ) => {
    return request<{ week: SocialWeek }>(
      `/api/social/week/${weekId}/channel-flow`,
      { method: 'PATCH', body: data, token }
    );
  },

  getStreak: (token: string) => {
    return request<{ streak: Streak }>('/api/social/streak', { token });
  },

  getHistory: (token: string, weeks?: number) => {
    const query = weeks ? `?weeks=${weeks}` : '';
    return request<{ weeks: SocialWeek[] }>(`/api/social/history${query}`, { token });
  },
};

export { ApiError };
